import { z } from 'zod';
/**
 * The vocabulary of the authorization layer, shared so the client can label a
 * locked control with the same reason string the server would return.
 *
 * The client NEVER decides access. It only decides whether to render a control
 * as available, locked-with-a-reason, or absent. Every route re-resolves the
 * same (user, resource, action) triple server-side.
 */
export const ACTIONS = [
    // course
    'course:read',
    'course:create',
    'course:update',
    'course:publish',
    'course:archive',
    'course:manage_enrollments',
    'course:view_analytics',
    'course:assign_admins',
    // session
    'session:read',
    'session:read_sibling',
    'session:create',
    'session:reorder',
    'session:update',
    'session:publish',
    'session:delete',
    'session:assign_instructors',
    // content
    'content:read',
    'content:update',
    // capstone
    'capstone:read',
    'capstone:create',
    'capstone:update',
    'capstone:publish',
    'capstone:assign_authors',
    // platform
    'user:read',
    'user:manage',
    'audit:read',
    'analytics:read_all',
];
/**
 * Guide actions live in their own list. Guides are platform-level — not inside
 * a course — so they resolve through `evaluateGuide`, not the course/session
 * evaluators, and the course matrix test (which is exhaustive over ACTIONS)
 * stays a statement about courses.
 */
export const GUIDE_ACTIONS = [
    'guide:read',
    'guide:create',
    'guide:update',
    'guide:publish',
    'guide:delete',
    'guide_section:manage',
];
/**
 * Human-readable denial reasons. The console shows these next to a locked
 * control instead of hiding it, which is what stops the "why can't I see it"
 * support loop.
 */
export const DENY_REASONS = {
    not_authenticated: 'Sign in to continue.',
    inactive_user: 'This account has been deactivated. Ask an admin to re-enable it.',
    not_enrolled: 'You are not enrolled in this course.',
    not_published: 'This content has not been published yet.',
    not_assigned: 'You are not assigned to this session.',
    admin_only: 'Only an admin can do this.',
    assigned_to_someone_else: 'Another instructor is assigned to this session.',
    course_unpublished: 'The course is still a draft, so learners cannot see this yet.',
    locked_sequential: 'Finish the previous session first — this course unlocks in order.',
    trainers_only: 'Only guide writers and admins can write guides. Ask an admin to make you a guide writer.',
    not_guide_author: 'Someone else wrote this guide. Ask them, or an admin, to change it.',
    view_as_read_only: 'You are viewing the platform as someone else, so nothing can be changed. Stop viewing as them to make changes.',
    not_found: 'Not found.',
};
const DENY_CODES = [
    'not_authenticated',
    'inactive_user',
    'not_enrolled',
    'not_published',
    'not_assigned',
    'admin_only',
    'assigned_to_someone_else',
    'course_unpublished',
    'locked_sequential',
    'trainers_only',
    'not_guide_author',
    'view_as_read_only',
    'not_found',
];
// Compile-time: DENY_CODES must name every DenyCode, or the wire schema below
// would reject a reason the server can legitimately send.
const _everyDenyCodeListed = {};
void _everyDenyCodeListed;
/** The wire shape of a guide's permission set, for response validation. */
export const guidePermissionSet = z.object({
    allowed: z.array(z.enum(GUIDE_ACTIONS)),
    denied: z.array(z.object({ action: z.enum(GUIDE_ACTIONS), code: z.enum(DENY_CODES), reason: z.string() })),
});
export function isAllowed(set, action) {
    return !!set && set.allowed.includes(action);
}
export function denialFor(set, action) {
    const hit = set?.denied.find((d) => d.action === action);
    return hit ? { code: hit.code, reason: hit.reason } : null;
}
//# sourceMappingURL=policy.js.map